import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { AdminPill } from '@/components/AdminPill';
import { Skeleton } from '@/components/Skeleton';
import { useAuth } from '@/contexts/AuthContext';
import {
  retryBitrixQueueRow,
  setBitrixEnabled,
  testBitrixConnection,
  type BitrixLogRow,
  type BitrixQueueRow,
  type BitrixSettings,
} from '@/lib/db/admin/bitrix24';
import { formatGstDateTimeIso } from '@/lib/format';

import { useBitrixLogs, useBitrixQueueRows, useBitrixSettings } from './AdminIntegrationsPage.data';

function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

export function AdminIntegrationsPage() {
  const { t } = useTranslation();
  const auth = useAuth();
  const role = auth.status === 'authenticated' ? auth.profile.role : null;
  const isSuperAdmin = role === 'super_admin';

  const settingsState = useBitrixSettings();
  const failedConfigState = useBitrixQueueRows('failed_config');
  const abandonedState = useBitrixQueueRows('abandoned');
  const logsState = useBitrixLogs(50);

  const [togglingEnabled, setTogglingEnabled] = useState(false);
  const [testing, setTesting] = useState(false);

  const onToggleEnabled = async () => {
    if (!settingsState.settings) return;
    const next = !settingsState.settings.enabled;
    setTogglingEnabled(true);
    try {
      await setBitrixEnabled(next);
      toast.success(
        next
          ? t('admin.integrations.bitrix.toasts.enabled')
          : t('admin.integrations.bitrix.toasts.disabled'),
      );
      settingsState.refetch();
    } catch (err) {
      toast.error(t('admin.integrations.bitrix.toasts.toggleFailed'));
      void err;
    } finally {
      setTogglingEnabled(false);
    }
  };

  const onTest = async () => {
    setTesting(true);
    try {
      const result = await testBitrixConnection();
      if (result.ok) {
        toast.success(
          t('admin.integrations.bitrix.toasts.testOk', {
            latency: result.latency_ms ?? '?',
          }),
        );
      } else {
        toast.error(
          t('admin.integrations.bitrix.toasts.testError', {
            error: result.error_message ?? 'unknown',
          }),
        );
      }
      settingsState.refetch();
    } catch (err) {
      toast.error(t('admin.integrations.bitrix.toasts.testFailed'));
      void err;
    } finally {
      setTesting(false);
    }
  };

  const onRetryRow = async (queueId: string, kind: 'failed_config' | 'abandoned') => {
    try {
      await retryBitrixQueueRow(queueId);
      toast.success(t('admin.integrations.bitrix.toasts.retryQueued'));
      if (kind === 'failed_config') {
        failedConfigState.refetch();
      } else {
        abandonedState.refetch();
      }
      logsState.refetch();
    } catch (err) {
      toast.error(t('admin.integrations.bitrix.toasts.retryFailed'));
      void err;
    }
  };

  return (
    <>
      <header className="admin-head">
        <div>
          <h1 className="admin-head__title">{t('admin.integrations.bitrix.title')}</h1>
          <p className="admin-head__sub">{t('admin.integrations.bitrix.subtitle')}</p>
        </div>
      </header>

      {settingsState.status === 'loading' ? (
        <Skeleton width="40%" height={14} />
      ) : settingsState.status === 'error' || !settingsState.settings ? (
        <p role="alert" className="admin-error">
          {t('admin.integrations.bitrix.errors.settings')}
        </p>
      ) : (
        <BitrixOverview
          settings={settingsState.settings}
          isSuperAdmin={isSuperAdmin}
          togglingEnabled={togglingEnabled}
          testing={testing}
          onToggle={onToggleEnabled}
          onTest={onTest}
        />
      )}

      {settingsState.settings ? <BitrixMappingPanel settings={settingsState.settings} /> : null}

      <BitrixQueueSection
        title={t('admin.integrations.bitrix.sections.configErrors')}
        hint={t('admin.integrations.bitrix.sections.configErrorsHint')}
        state={failedConfigState}
        onRetry={(id) => onRetryRow(id, 'failed_config')}
        emptyLabel={t('admin.integrations.bitrix.sections.configErrorsEmpty')}
        retryLabel={t('admin.integrations.bitrix.actions.retryFix')}
      />

      <BitrixQueueSection
        title={t('admin.integrations.bitrix.sections.abandoned')}
        hint={t('admin.integrations.bitrix.sections.abandonedHint')}
        state={abandonedState}
        onRetry={(id) => onRetryRow(id, 'abandoned')}
        emptyLabel={t('admin.integrations.bitrix.sections.abandonedEmpty')}
        retryLabel={t('admin.integrations.bitrix.actions.retry')}
      />

      <BitrixLogsSection state={logsState} />
    </>
  );
}

// ----------------------------------------------------------------------------
// Overview — enabled toggle, secret status, last-test, action buttons
// ----------------------------------------------------------------------------

type OverviewProps = {
  settings: BitrixSettings;
  isSuperAdmin: boolean;
  togglingEnabled: boolean;
  testing: boolean;
  onToggle: () => void;
  onTest: () => void;
};

function BitrixOverview({
  settings,
  isSuperAdmin,
  togglingEnabled,
  testing,
  onToggle,
  onTest,
}: OverviewProps) {
  const { t } = useTranslation();
  const lastStatus = settings.last_test_status;
  return (
    <section className="admin-card" style={{ padding: 24, marginBottom: 24 }}>
      <div className="admin-section__label">{t('admin.integrations.bitrix.sections.status')}</div>

      <dl className="admin-kv-list" style={{ display: 'grid', gap: 12, margin: 0 }}>
        <div>
          <dt>{t('admin.integrations.bitrix.fields.status')}</dt>
          <dd>
            <AdminPill
              status={settings.enabled ? 'ACTIVE' : 'ARCHIVED'}
              label={
                settings.enabled
                  ? t('admin.integrations.bitrix.statusEnabled')
                  : t('admin.integrations.bitrix.statusDisabled')
              }
            />
          </dd>
        </div>
        <div>
          <dt>{t('admin.integrations.bitrix.fields.webhookUrl')}</dt>
          <dd>{t('admin.integrations.bitrix.webhookManaged')}</dd>
        </div>
        <div>
          <dt>{t('admin.integrations.bitrix.fields.lastTest')}</dt>
          <dd>
            {settings.last_test_at ? (
              <>
                {formatGstDateTimeIso(settings.last_test_at)} ·{' '}
                <strong
                  style={{
                    color: lastStatus === 'ok' ? 'var(--ddm-positive)' : 'var(--ddm-warning)',
                  }}
                >
                  {lastStatus === 'ok'
                    ? t('admin.integrations.bitrix.testOk')
                    : t('admin.integrations.bitrix.testError')}
                </strong>
                {settings.last_test_error ? (
                  <div
                    style={{ fontSize: '0.78rem', color: 'var(--ddm-neutral-400)', marginTop: 4 }}
                  >
                    {settings.last_test_error}
                  </div>
                ) : null}
              </>
            ) : (
              t('admin.integrations.bitrix.testNever')
            )}
          </dd>
        </div>
        <div>
          <dt>{t('admin.integrations.bitrix.fields.updatedAt')}</dt>
          <dd>{formatGstDateTimeIso(settings.updated_at)}</dd>
        </div>
      </dl>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 20 }}>
        <button
          type="button"
          className="admin-btn"
          onClick={onToggle}
          disabled={!isSuperAdmin || togglingEnabled}
          aria-disabled={!isSuperAdmin || togglingEnabled}
          title={isSuperAdmin ? undefined : t('admin.integrations.bitrix.actions.superAdminOnly')}
        >
          {settings.enabled
            ? t('admin.integrations.bitrix.actions.disable')
            : t('admin.integrations.bitrix.actions.enable')}
        </button>
        <button
          type="button"
          className="admin-btn admin-btn--outline"
          onClick={onTest}
          disabled={!isSuperAdmin || testing}
          aria-disabled={!isSuperAdmin || testing}
          title={isSuperAdmin ? undefined : t('admin.integrations.bitrix.actions.superAdminOnly')}
        >
          {testing
            ? t('admin.integrations.bitrix.actions.testing')
            : t('admin.integrations.bitrix.actions.test')}
        </button>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Mapping panel — read-only display of source_ids + uf_field_map + retry
// ----------------------------------------------------------------------------

function BitrixMappingPanel({ settings }: { settings: BitrixSettings }) {
  const { t } = useTranslation();
  const config = settings.config;
  return (
    <section className="admin-card" style={{ padding: 24, marginBottom: 24 }}>
      <div className="admin-section__label">{t('admin.integrations.bitrix.sections.mapping')}</div>
      <p style={{ fontSize: '0.78rem', color: 'var(--ddm-neutral-400)', marginBottom: 12 }}>
        {t('admin.integrations.bitrix.sections.mappingHint')}
      </p>

      <h3 style={{ fontSize: '0.85rem', margin: '14px 0 8px' }}>
        {t('admin.integrations.bitrix.mapping.sourceIds')}
      </h3>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.integrations.bitrix.mapping.form')}</th>
              <th>{t('admin.integrations.bitrix.mapping.sourceId')}</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(config.source_ids).map(([form, sourceId]) => (
              <tr key={form}>
                <td>{form}</td>
                <td>
                  <code>{sourceId}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 style={{ fontSize: '0.85rem', margin: '20px 0 8px' }}>
        {t('admin.integrations.bitrix.mapping.ufFields')}
      </h3>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.integrations.bitrix.mapping.key')}</th>
              <th>{t('admin.integrations.bitrix.mapping.bitrixField')}</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(config.uf_field_map).map(([key, field]) => (
              <tr key={key}>
                <td>{key}</td>
                <td>
                  <code>{field}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 style={{ fontSize: '0.85rem', margin: '20px 0 8px' }}>
        {t('admin.integrations.bitrix.mapping.retry')}
      </h3>
      <dl className="admin-kv-list" style={{ display: 'grid', gap: 8, margin: 0 }}>
        <div>
          <dt>{t('admin.integrations.bitrix.mapping.maxAttempts')}</dt>
          <dd>{config.retry.max_attempts}</dd>
        </div>
        <div>
          <dt>{t('admin.integrations.bitrix.mapping.backoff')}</dt>
          <dd>
            <code>{config.retry.backoff_seconds.join(', ')} s</code>
          </dd>
        </div>
        <div>
          <dt>{t('admin.integrations.bitrix.mapping.webhookTimeout')}</dt>
          <dd>{config.webhook_timeout_ms} ms</dd>
        </div>
        <div>
          <dt>{t('admin.integrations.bitrix.mapping.lockSeconds')}</dt>
          <dd>{config.lock_seconds} s</dd>
        </div>
        <div>
          <dt>{t('admin.integrations.bitrix.mapping.batchSize')}</dt>
          <dd>{config.batch_size}</dd>
        </div>
      </dl>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Queue section — failed_config + abandoned (with retry button)
// ----------------------------------------------------------------------------

type QueueSectionProps = {
  title: string;
  hint: string;
  state: { status: 'loading' | 'success' | 'error'; rows: BitrixQueueRow[]; error: Error | null };
  onRetry: (queueId: string) => void;
  emptyLabel: string;
  retryLabel: string;
};

function BitrixQueueSection({
  title,
  hint,
  state,
  onRetry,
  emptyLabel,
  retryLabel,
}: QueueSectionProps) {
  const { t } = useTranslation();
  return (
    <section className="admin-card" style={{ padding: 24, marginBottom: 24 }}>
      <div className="admin-section__label">{title}</div>
      <p style={{ fontSize: '0.78rem', color: 'var(--ddm-neutral-400)', marginBottom: 12 }}>
        {hint}
      </p>
      {state.status === 'loading' ? (
        <Skeleton width="40%" height={14} />
      ) : state.status === 'error' ? (
        <p role="alert" className="admin-error">
          {t('admin.integrations.bitrix.errors.queue')}
        </p>
      ) : state.rows.length === 0 ? (
        <p style={{ color: 'var(--ddm-neutral-400)' }}>{emptyLabel}</p>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>{t('admin.integrations.bitrix.queue.source')}</th>
                <th>{t('admin.integrations.bitrix.queue.reference')}</th>
                <th>{t('admin.integrations.bitrix.queue.attempts')}</th>
                <th>{t('admin.integrations.bitrix.queue.lastError')}</th>
                <th>{t('admin.integrations.bitrix.queue.updatedAt')}</th>
                <th>{t('admin.integrations.bitrix.queue.action')}</th>
              </tr>
            </thead>
            <tbody>
              {state.rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.source_table}</td>
                  <td>
                    <code>{shortId(row.source_id)}</code>
                  </td>
                  <td>
                    {row.attempts} / {row.max_attempts}
                  </td>
                  <td style={{ maxWidth: 320 }}>
                    {row.last_error_message ? (
                      <span title={row.last_error_message}>{row.last_error_message}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{formatGstDateTimeIso(row.updated_at)}</td>
                  <td>
                    <button
                      type="button"
                      className="admin-btn admin-btn--outline"
                      onClick={() => onRetry(row.id)}
                    >
                      {retryLabel}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ----------------------------------------------------------------------------
// Sync logs section — last 50 integration_logs rows (read-only)
// ----------------------------------------------------------------------------

function BitrixLogsSection({
  state,
}: {
  state: { status: 'loading' | 'success' | 'error'; rows: BitrixLogRow[]; error: Error | null };
}) {
  const { t } = useTranslation();
  return (
    <section className="admin-card" style={{ padding: 24, marginBottom: 24 }}>
      <div className="admin-section__label">{t('admin.integrations.bitrix.sections.logs')}</div>
      <p style={{ fontSize: '0.78rem', color: 'var(--ddm-neutral-400)', marginBottom: 12 }}>
        {t('admin.integrations.bitrix.sections.logsHint')}
      </p>
      {state.status === 'loading' ? (
        <Skeleton width="40%" height={14} />
      ) : state.status === 'error' ? (
        <p role="alert" className="admin-error">
          {t('admin.integrations.bitrix.errors.logs')}
        </p>
      ) : state.rows.length === 0 ? (
        <p style={{ color: 'var(--ddm-neutral-400)' }}>
          {t('admin.integrations.bitrix.sections.logsEmpty')}
        </p>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>{t('admin.integrations.bitrix.logs.date')}</th>
                <th>{t('admin.integrations.bitrix.logs.action')}</th>
                <th>{t('admin.integrations.bitrix.logs.status')}</th>
                <th>{t('admin.integrations.bitrix.logs.httpStatus')}</th>
                <th>{t('admin.integrations.bitrix.logs.error')}</th>
              </tr>
            </thead>
            <tbody>
              {state.rows.map((row) => (
                <tr key={row.id}>
                  <td>{formatGstDateTimeIso(row.created_at)}</td>
                  <td>{row.action}</td>
                  <td>
                    <AdminPill
                      status={row.status === 'success' ? 'ACTIVE' : 'REFUSED'}
                      label={row.status}
                    />
                  </td>
                  <td>{row.http_status ?? '—'}</td>
                  <td style={{ maxWidth: 320 }}>
                    {row.error_message ? (
                      <span title={row.error_message}>{row.error_message}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default AdminIntegrationsPage;
